import React, {useState, useCallback, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useFormState} from "../../../hooks/useFormState";
import Button from "../../../ui/button";
import Input from "../../../ui/input";
import Select from "../../../ui/select";
import classes from "./AddOfflineTestSessionModal.module.css";
import {useMutation, useQuery,gql} from "@apollo/client";
import {
  addOfflineTestSessionMutation,
} from "../../../scenes/account/offline-test-sessions.graphql";
import {Challenge, User} from "../../../schema";

const challengesQuery = gql`
  query GetChallengesForOfflineTest {
    challenges: getChallenges (flatMode: true) {
      ... on Challenge {
        id
        name
        projectName
      }
    }
  }
`;

const usersQuery = gql`
  query GetUsersForOfflineTest {
    users: getCurrentAccountUsers {
      id
      fullName
      email
    }
  }
`;

function AddOfflineTestSessionModalHeader() {
  const {t} = useTranslation();

  return <span>{t("components.AddOfflineTestSessionModal.title")}</span>;
}

type ContentProps = {
  onSuccess?: () => void;
};

function AddOfflineTestSessionModalContent({onSuccess}: ContentProps) {
  const {t} = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {data: challengesData} = useQuery<{challenges: Array<Challenge | {__typename: string}>}>(challengesQuery);
  const {data: usersData} = useQuery<{users: User[]}>(usersQuery);

  const challenges = challengesData?.challenges.filter(
    (item): item is Challenge => item.__typename === "Challenge"
  ) || [];

  const [addOfflineTestSession] = useMutation(addOfflineTestSessionMutation);

  const formState = useFormState<{
    name: string;
    userId: string;
    challengeId: string;
  }>({
    initialValues: {
      name: "",
      userId: "",
      challengeId: "",
    },
    preventDefault: true,
    onSubmit: async (data) => {
      if (!selectedFile) {
        return;
      }

      setUploading(true);
      try {
        await addOfflineTestSession({
          variables: {
            data: {
              name: data.name || undefined,
              userId: data.userId || undefined,
              challengeId: data.challengeId,
              audio: selectedFile,
            },
          },
        });
        onSuccess && onSuccess();
      } catch (error) {
        console.error("Error creating offline test session:", error);
      } finally {
        setUploading(false);
      }
    },
  });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const submitDisabled = !selectedFile || !formState.values.challengeId || uploading;

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler}>
      <div className={classes.field}>
        <label className={classes.label}>{t("components.AddOfflineTestSessionModal.name")}</label>
        <Input
          className={classes.input}
          name="name"
          placeholder={t("components.AddOfflineTestSessionModal.namePlaceholder")}
          onChange={formState.changeHandler}
          value={formState.values.name}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label}>
          {t("components.AddOfflineTestSessionModal.user")}{" "}
          <span className={classes.optional}>({t("common.optional")})</span>
        </label>
        <Select
          className={classes.input}
          name="userId"
          value={formState.values.userId}
          onChange={formState.changeHandler}
        >
          <option value="">{t("components.AddOfflineTestSessionModal.selectUser")}</option>
          {usersData?.users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.fullName} ({user.email})
            </option>
          ))}
        </Select>
      </div>

      <div className={classes.field}>
        <label className={classes.label}>
          {t("components.AddOfflineTestSessionModal.challenge")} <span className={classes.required}>*</span>
        </label>
        <Select
          className={classes.input}
          name="challengeId"
          value={formState.values.challengeId}
          onChange={formState.changeHandler}
          required
        >
          <option value="">{t("components.AddOfflineTestSessionModal.selectChallenge")}</option>
          {challenges.map((challenge) => (
            <option key={challenge.id} value={challenge.id}>
              {challenge.name} {challenge.projectName
              ? `(${t("components.AddOfflineTestSessionModal.project")}: ${challenge.projectName})`
              : ""}
            </option>
          ))}
        </Select>
      </div>

      <div className={classes.field}>
        <label className={classes.label}>
          {t("components.AddOfflineTestSessionModal.audio")} <span className={classes.required}>*</span>
        </label>
        <div className={classes.fileInputWrapper}>
          <Button as="label" className={classes.fileButton}>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              className={classes.fileInput}
              required
            />
            {selectedFile
              ? t("components.AddOfflineTestSessionModal.selectAnotherFile")
              : t("components.AddOfflineTestSessionModal.selectFile")}
          </Button>
          {selectedFile && (
            <div className={classes.fileInfo}>
              <div className={classes.fileInfoContent}>
                <span className={classes.fileName}>{selectedFile.name}</span>
                <span className={classes.fileSize}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <Button
                type="button"
                className={classes.removeFileButton}
                color="danger"
                size="s"
                onClick={handleRemoveFile}
              >
                {t("common.delete")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={classes.bottom}>
        <Button type="submit" color={submitDisabled ? "secondary" : "success"} disabled={submitDisabled}>
          {uploading
            ? t("components.AddOfflineTestSessionModal.uploading")
            : t("components.AddOfflineTestSessionModal.submit")}
        </Button>
      </div>
    </form>
  );
}

const AddOfflineTestSessionModal = {
  Header: AddOfflineTestSessionModalHeader,
  Content: AddOfflineTestSessionModalContent,
};

export default AddOfflineTestSessionModal;

